import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Inbox, AlertTriangle, ShieldCheck } from "lucide-react";

const fmtAgo = (iso) => {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (!then || Number.isNaN(then)) return "—";
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

/**
 * IMAP Health Tile — surfaces the last successful Zoho Mail poll and
 * flags long silences / auth errors so ops see it before buyers do.
 * Persisted server-side in db.settings._id="zoho_imap_health" by the
 * 3-min APScheduler job in services/zoho_imap_poller.py.
 */
export function AdminIMAPHealthTile() {
  const { api } = useAuth();
  const [d, setD] = useState(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const load = () => api.get("/admin/imap/health").then((r) => setD(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 45_000);
    const tick = setInterval(() => setNow(Date.now()), 15_000);
    return () => { clearInterval(t); clearInterval(tick); };
  }, [api]);

  // Stale threshold — no OK ping in 10 minutes = warning
  const okAt = d?.last_ok_at;
  const staleMs = okAt ? (now - new Date(okAt).getTime()) : Infinity;
  const state = d?.state || "unknown";
  const stale = state === "ok" && staleMs > 10 * 60 * 1000;
  const colour = state === "error" ? "#C0392B"
              : stale ? "#B87333"
              : state === "ok" ? "#5EE6D0"
              : "#8A8A82";
  const Icon = state === "error" || stale ? AlertTriangle : ShieldCheck;
  const label = state === "error" ? "IMAP disconnected"
              : stale ? "Poll gone stale"
              : state === "ok" ? "IMAP healthy"
              : "IMAP status unknown";

  return (
    <div data-testid="admin-imap-health-tile"
         className={`w-full rounded-sm border p-6 md:p-7 mb-4 transition-colors ${state === "ok" && !stale ? "border-[#5EE6D0]/50 bg-[#5EE6D0]/[0.05]" : state === "error" ? "border-[#C0392B]/50 bg-[#C0392B]/[0.05]" : "border-white/10 bg-black/40"}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <span className="grid place-items-center w-9 h-9 rounded-full text-black shrink-0"
                style={{ background: state === "ok" && !stale ? "linear-gradient(135deg,#5EE6D0,#3CBFAE)"
                                                  : state === "error" ? "#C0392B"
                                                  : "#1a1a1a",
                          boxShadow: state === "ok" && !stale ? "0 0 22px rgba(60,191,174,0.45)"
                                       : state === "error" ? "0 0 22px rgba(192,57,43,0.45)"
                                       : "none",
                          color: state === "ok" || state === "error" ? "#000" : "#5A5A55" }}>
            <Icon size={15} />
          </span>
          <div className="min-w-0">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.32em]" style={{ color: colour }}>
              · Zoho Mail · Inbound SRN poller ·
            </div>
            <div className="font-serif text-lg md:text-xl text-[#F5F5F0] mt-1 flex items-center gap-2 flex-wrap">
              <Inbox size={14} style={{ color: colour }} />
              {label}
              {okAt && (
                <span className="font-body text-xs text-[#8A8A82] ml-1" data-testid="imap-last-ok">
                  · last successful poll <b style={{ color: colour }}>{fmtAgo(okAt)}</b>
                </span>
              )}
            </div>
          </div>
        </div>
        {d?.fetched != null && (
          <div className="text-right shrink-0" data-testid="imap-throughput">
            <div className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#8A8A82]">Last tick</div>
            <div className="font-body text-xs text-[#C9C9C2] tabular-nums">
              fetched <b className="text-[#F5F5F0]">{d.fetched}</b>
              {" · minted "}<b className="text-[#5EE6D0]">{d.minted || 0}</b>
              {" · matched "}<b className="text-[#F5F5F0]">{d.matched || 0}</b>
              {(d.failed || 0) > 0 && <> · <b className="text-[#C0392B]">{d.failed} failed</b></>}
            </div>
          </div>
        )}
      </div>
      {state === "error" && d?.last_error && (
        <div className="mt-4 pt-3 border-t border-[#C0392B]/25 font-body text-xs text-[#F5D67A]">
          <span className="uppercase tracking-[0.24em] text-[10px] text-[#C0392B] mr-2">Zoho reply</span>
          <span className="text-[#C9C9C2]">{d.last_error}</span>
          <div className="mt-2 text-[11px] text-[#8A8A82]">
            Fix: Zoho Mail admin console → Settings → Mail Accounts → IMAP → Enable, then wait ≤ 3 minutes for the next tick.
          </div>
        </div>
      )}
    </div>
  );
}
